import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Github, Twitter, Linkedin, Mail } from 'lucide-react';
const sections = [{
  title: '1. Services',
  content: `Tap Theory provides freelance mobile application development and consulting services, including but not limited to:

• iOS and Android application design and development
• Cross-platform mobile development
• Technical consulting and product strategy
• App maintenance and support

The specific scope, deliverables, timeline, and fees for each project will be defined in a separate project agreement or statement of work.`
}, {
  title: '2. Engagement & Communication',
  content: `By contacting us through our website or other channels, you agree to provide accurate and complete information. We will use the information you provide solely for the purpose of responding to your inquiry and, if applicable, delivering our services.

All project engagements are subject to mutual agreement on scope, timeline, and compensation before work begins.`
}, {
  title: '3. Intellectual Property',
  content: `Unless otherwise agreed in writing:

• All intellectual property created during a project is assigned to the client upon full payment
• Tap Theory retains the right to use general knowledge, techniques, and non-confidential elements for future work
• Tap Theory may include completed projects in its portfolio unless the client requests otherwise
• Pre-existing tools, libraries, and frameworks used remain the property of their respective owners`
}, {
  title: '4. Payment Terms',
  content: `Payment terms will be specified in individual project agreements. General terms include:

• A deposit may be required before work begins
• Invoices are due within 30 days of issuance unless otherwise agreed
• Late payments may incur interest at the statutory rate under Belgian law
• Tap Theory reserves the right to pause work on overdue accounts`
}, {
  title: '5. Confidentiality',
  content: `Both parties agree to keep confidential any proprietary or sensitive information shared during the course of a project. This obligation survives the termination of any agreement between the parties.

Confidential information does not include information that is publicly available, independently developed, or lawfully obtained from a third party.`
}, {
  title: '6. Limitation of Liability',
  content: `To the maximum extent permitted by law:

• Tap Theory's total liability for any claim arising from our services shall not exceed the total fees paid for the specific project in question
• Tap Theory is not liable for indirect, incidental, consequential, or punitive damages
• Tap Theory is not responsible for third-party services, APIs, or platforms integrated into a project
• Clients are responsible for ensuring their use of delivered work complies with applicable laws`
}, {
  title: '7. Warranties & Disclaimers',
  content: `Tap Theory strives to deliver high-quality work. However:

• Services are provided "as is" without warranties of any kind, express or implied
• We do not guarantee that applications will be free of bugs or errors after delivery
• A reasonable warranty period for bug fixes will be defined in individual project agreements
• We do not warrant uninterrupted availability of any deployed application`
}, {
  title: '8. Termination',
  content: `Either party may terminate a project engagement with written notice as specified in the project agreement. Upon termination:

• The client is responsible for payment of all work completed up to the termination date
• All deliverables completed and paid for will be transferred to the client
• Confidentiality obligations remain in effect`
}, {
  title: '9. Governing Law',
  content: `These Terms of Use are governed by and construed in accordance with the laws of Belgium. Any disputes arising from these terms or our services shall be subject to the exclusive jurisdiction of the courts of Belgium.`
}, {
  title: '10. Changes to These Terms',
  content: `We reserve the right to update these Terms of Use at any time. Changes will be posted on this page with an updated effective date. Continued use of our services after changes constitutes acceptance of the revised terms.`
}, {
  title: '11. Contact',
  content: `For questions about these Terms of Use, please contact us at:

Tap Theory
Email: hello@taptheory.be
Website: taptheory.be`
}];
export function TermsPage() {
  return <div className="min-h-screen bg-background font-sans text-text-main flex flex-col" data-id="element-292">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-id="element-293">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-294">
          <div className="flex justify-between items-center h-20" data-id="element-295">
            <Link to="/" className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-296">
              taptheory<span className="text-primary" data-id="element-297">.be</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-298">
              <ArrowLeft className="w-4 h-4" data-id="element-299" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="flex-1 pt-32 pb-24" data-id="element-300">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-301">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-12" data-id="element-302">
            <span className="font-mono text-sm font-bold text-primary tracking-widest uppercase block mb-3" data-id="element-303">
              Legal
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-304">
              Terms of Use
            </h1>
            <p className="text-gray-400" data-id="element-305">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="mb-10 text-gray-300 leading-relaxed" data-id="element-306">
            These Terms of Use ("Terms") govern your use of the taptheory.be
            website and the services provided by Tap Theory. By accessing our
            website or engaging our services, you agree to be bound by these
            Terms.
          </motion.div>

          <div className="space-y-10" data-id="element-307">
            {sections.map((section, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.15 + i * 0.05
          }} data-id="element-308">
                <h2 className="text-xl font-bold text-white mb-4" data-id="element-309">
                  {section.title}
                </h2>
                <p className="text-gray-400 leading-relaxed whitespace-pre-line" data-id="element-310">
                  {section.content}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-black" data-id="element-311">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-312">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-313">
            <span className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-314">
              taptheory<span className="text-primary" data-id="element-315">.be</span>
            </span>
            <div className="flex space-x-8 text-sm font-medium text-gray-400" data-id="element-316">
              <Link to="/contact" className="hover:text-primary transition-colors" data-id="element-317">
                Contact
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors" data-id="element-318">
                Privacy
              </Link>
              <Link to="/terms" className="text-primary transition-colors" data-id="element-319">
                Terms
              </Link>
            </div>
            <div className="flex space-x-6" data-id="element-320">
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-321">
                <Github className="w-5 h-5" data-id="element-322" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-323">
                <Twitter className="w-5 h-5" data-id="element-324" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-325">
                <Linkedin className="w-5 h-5" data-id="element-326" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-327">
                <Mail className="w-5 h-5" data-id="element-328" />
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-600 font-mono" data-id="element-329">
            &copy; {new Date().getFullYear()} Tap Theory. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}